import { useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import { useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import {
  Col,
  Form,
  FormGroup,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap';
import { ErrorMessage } from '@hookform/error-message';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import Toggle from 'react-toggle';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

import {
  Card,
  CardBody,
  CardHeader,
} from '../../../../_metronic/_partials/controls';
import { getDemoMode } from '../../../../helpers';
import {
  useGetRevenueSettings,
  useUpdateRevenueSettings,
} from '../../../../swrHelpers/settings/revenueSettings';
import SponserSubscriptionsList from './SponserSubscriptionsList';
import { CryptoRevenueForm } from '../../Settings/Revenue/CryptoRevenueForm';
import * as actions from '../../../../store/actions/index';
import { TokenizationRevenueForm } from '../../Settings/Revenue/TokenizationRevenueForm';

export const SponsorFees = () => {
  const dispatch = useDispatch();
  const demoMode = getDemoMode();
  const MySwal = withReactContent(Swal);
  const intl = useIntl();
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ mode: 'onChange' });

  const { payments } = useSelector((state) => state.paymentGateway);
  const { generalSettings } = useSelector((state) => state.settings);
  const { taxonomy } = useSelector((state) => state?.taxonomy);

  const investType =
    generalSettings?.mangoPayModule !== 'yes'
      ? [
          {
            value: 'fixed',
            label: 'Fixed',
          },
          {
            value: 'flexible',
            label: 'Flexible',
          },
          {
            value: 'both',
            label: 'Both',
          },
        ]
      : [
          {
            value: 'flexible',
            label: 'Flexible',
          },
        ];

  const { data: revenueSettings } = useGetRevenueSettings();

  const { trigger, isMutating } = useUpdateRevenueSettings();

  const alertBox = (title, text, icon) => {
    MySwal.fire({
      title: title,
      text: text,
      icon: icon,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: true,
      timer: 3000,
    });
  };

  useEffect(() => {
    dispatch(actions.getPaymentGateway('stripe'));
  }, []);

  useEffect(() => {
    reset({
      id: revenueSettings?.id || undefined,
      investType: revenueSettings?.investType || '',
      percentageCampaignCommission:
        revenueSettings?.percentageCampaignCommission || '',
      fixedCampaignCommission: revenueSettings?.fixedCampaignCommission || '',
      percentageSuccessfulCommission:
        revenueSettings?.percentageSuccessfulCommission || '',
      fixedSuccessfulCommission:
        revenueSettings?.fixedSuccessfulCommission || '',
      percentageUnsuccessfulCommission:
        revenueSettings?.percentageUnsuccessfulCommission || '',
      fixedUnsuccessfulCommission:
        revenueSettings?.fixedUnsuccessfulCommission || '',
      sponserMarketingResourceFeeStatus:
        revenueSettings?.sponserMarketingResourceFeeStatus === 'YES' || false,
      sponserMarketingResourceFlatFees:
        revenueSettings?.sponserMarketingResourceFlatFees || '',
      sponserMarketingResourceDescription:
        revenueSettings?.sponserMarketingResourceDescription || '',
      sponserSalesResourceFeeStatus:
        revenueSettings?.sponserSalesResourceFeeStatus === 'YES' || false,
      sponserSalesResourceFlatFees:
        revenueSettings?.sponserSalesResourceFlatFees || '',
      sponserSalesResourceDescription:
        revenueSettings?.sponserSalesResourceDescription || '',
      sponserListingFeeStatus:
        revenueSettings?.sponserListingFeeStatus === 'YES' || false,
      sponserListingFeeType: revenueSettings?.sponserListingFeeType || '',
      sponserListingFlatFees: revenueSettings?.sponserListingFlatFees || '',
      sponserSubscriptionDuration:
        revenueSettings?.sponserSubscriptionDuration || '',
      walletAutoDeductStatus:
        revenueSettings?.walletAutoDeductStatus === 'YES' || false,
      stripeAutoDeductStatus:
        revenueSettings?.stripeAutoDeductStatus === 'YES' || false,
    });
  }, [revenueSettings]);

  const onSubmit = async (data) => {
    data.sponserMarketingResourceFeeStatus =
      data?.sponserMarketingResourceFeeStatus ? 'YES' : 'NO';
    data.sponserSalesResourceFeeStatus = data?.sponserSalesResourceFeeStatus
      ? 'YES'
      : 'NO';
    data.sponserListingFeeStatus = data?.sponserListingFeeStatus ? 'YES' : 'NO';
    data.walletAutoDeductStatus = data?.walletAutoDeductStatus ? 'YES' : 'NO';
    data.stripeAutoDeductStatus = data?.stripeAutoDeductStatus ? 'YES' : 'NO';

    try {
      const response = await trigger(data);

      if (response?.id) {
        alertBox('Sponsor Fees Updated Successfully!', '', 'success');
      } else {
        alertBox('Sponsor Fees Update Failed!', '', 'error');
      }
    } catch (error) {
      console.error('RevenueSettings update error:', error);
    }
  };

  return (
    <>
      {generalSettings?.paymentMethod !== 'crypto-payment' ? (
        <>
          <Card>
            <CardHeader title='Sponser Fees' />
            <CardBody className='pt-0 px-4 py-6'>
              <Row className='justify-content-md-center'>
                <Col md={11}>
                  <Form
                    id='revenueSettings'
                    className='form form-label-right'
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    <div className='mb-6 bg-table-bg-clr p-4 rounded-fix'>
                      <div className='form-group floating-input-field-wrapper mb-6'>
                        <label className='floating-input-field-label-static start-0 ms-4'>
                          {intl.formatMessage(
                            { id: 'REVENUE.TITLE' },
                            { value: taxonomy?.projectSingular },
                          )}
                          <span className='text-danger ms-1'>*</span>
                        </label>
                        <Controller
                          name={'investType'}
                          control={control}
                          rules={{ required: 'This field is required' }}
                          render={({ field: { value, onChange } }) => {
                            return (
                              <Select
                                placeholder={'Select Invest Type'}
                                classNamePrefix='react-select'
                                className={classNames(
                                  'react-select-container',
                                  {
                                    'border border-danger rounded-fix':
                                      errors?.investType,
                                  },
                                )}
                                styles={{
                                  control: (base, state) => ({
                                    ...base,
                                    background: 'white !important',
                                  }),
                                }}
                                options={investType}
                                value={
                                  investType.find((c) => c.value === value) ||
                                  null
                                }
                                onChange={(val) => onChange(val.value)}
                              />
                            );
                          }}
                        />
                        <ErrorMessage
                          errors={errors}
                          name='investType'
                          render={({ message }) => (
                            <div className='invalid-feedback d-block ms-1'>
                              {message}
                            </div>
                          )}
                        />
                      </div>
                      {['fixed', 'both'].includes(watch('investType')) && (
                        <div className='row'>
                          <div className='form-group ms-2 mb-1'>
                            <label className='font-size-sm text-black'>
                              {intl.formatMessage(
                                { id: 'FIXED.COMMISSION' },
                                { value: taxonomy?.projectSingular },
                              )}
                              <span className='text-important ms-1'>*</span>
                            </label>
                          </div>
                          <div className='col-md-6 form-group mb-6'>
                            <FormGroup className='px-4'>
                              <Row>
                                <Col
                                  xs={'auto'}
                                  className='d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium'
                                >
                                  <label id='percentageCampaignCommission'>{`Percentage (%)`}</label>
                                </Col>
                                <Col className='p-0'>
                                  <Form.Control
                                    name='percentageCampaignCommission'
                                    className={classNames(
                                      'form-control rounded-0 rounded-end text-black bg-white',
                                      {
                                        'is-invalid':
                                          errors.percentageCampaignCommission,
                                      },
                                    )}
                                    {...register(
                                      'percentageCampaignCommission',
                                      {
                                        required: 'This field is required',
                                        pattern: {
                                          value: /^(\d+(\.\d*)?|\.\d+)$/, // ✅ allows 123, 123.45, .45
                                          message: 'Please enter valid value',
                                        },
                                      },
                                    )}
                                  />
                                </Col>
                              </Row>
                            </FormGroup>
                            <ErrorMessage
                              errors={errors}
                              name='percentageCampaignCommission'
                              render={({ message }) => (
                                <div className='invalid-feedback d-block ms-1'>
                                  {message}
                                </div>
                              )}
                            />
                          </div>
                          <div className='col-md-6 form-group mb-6'>
                            <FormGroup className='px-4'>
                              <Row>
                                <Col
                                  xs={'auto'}
                                  className='d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium'
                                >
                                  <label id='fixedCampaignCommission'>{`Fixed (${generalSettings.currencyId?.symbol})`}</label>
                                </Col>
                                <Col className='p-0'>
                                  <Form.Control
                                    name='fixedCampaignCommission'
                                    className={classNames(
                                      'form-control rounded-0 rounded-end text-black bg-white',
                                      {
                                        'is-invalid':
                                          errors.fixedCampaignCommission,
                                      },
                                    )}
                                    {...register('fixedCampaignCommission', {
                                      required: 'This field is required',
                                      pattern: {
                                        value: /^(\d+(\.\d*)?|\.\d+)$/, // ✅ allows 123, 123.45, .45
                                        message: 'Please enter valid value',
                                      },
                                    })}
                                  />
                                </Col>
                              </Row>
                            </FormGroup>
                            <ErrorMessage
                              errors={errors}
                              name='fixedCampaignCommission'
                              render={({ message }) => (
                                <div className='invalid-feedback d-block ms-1'>
                                  {message}
                                </div>
                              )}
                            />
                          </div>
                        </div>
                      )}
                      {['flexible', 'both'].includes(watch('investType')) && (
                        <>
                          <div className='row'>
                            <div className='ms-2 mb-1'>
                              <label className='font-size-sm text-black'>
                                {intl.formatMessage(
                                  {
                                    id:
                                      generalSettings?.mangoPayModule !== 'yes'
                                        ? `FLEX.SUCC.CAMP`
                                        : `MANGOPAY.FLEX.SUCC.CAMP`,
                                  },
                                  { value: taxonomy?.projectSingular },
                                )}
                                <span className='text-important ms-1'>*</span>
                              </label>
                            </div>
                            <div className='col-md-6 form-group mb-6'>
                              <FormGroup className='px-4'>
                                <Row>
                                  <Col
                                    xs={'auto'}
                                    className='d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium'
                                  >
                                    <label id='percentageSuccessfulCommission'>{`Percentage (%)`}</label>
                                  </Col>
                                  <Col className='p-0'>
                                    <Form.Control
                                      name='percentageSuccessfulCommission'
                                      className={classNames(
                                        'form-control rounded-0 rounded-end text-black bg-white',
                                        {
                                          'is-invalid':
                                            errors.percentageSuccessfulCommission,
                                        },
                                      )}
                                      {...register(
                                        'percentageSuccessfulCommission',
                                        {
                                          required: 'This field is required',
                                          pattern: {
                                            value: /^(\d+(\.\d*)?|\.\d+)$/, // ✅ allows 123, 123.45, .45
                                            message: 'Please enter valid value',
                                          },
                                        },
                                      )}
                                    />
                                  </Col>
                                </Row>
                              </FormGroup>
                              <ErrorMessage
                                errors={errors}
                                name='percentageSuccessfulCommission'
                                render={({ message }) => (
                                  <div className='invalid-feedback d-block ms-1'>
                                    {message}
                                  </div>
                                )}
                              />
                            </div>
                            <div className='col-md-6 form-group mb-6'>
                              <FormGroup className='px-4'>
                                <Row>
                                  <Col
                                    xs={'auto'}
                                    className='d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium'
                                  >
                                    <label id='fixedSuccessfulCommission'>{`Fixed (${generalSettings.currencyId?.symbol})`}</label>
                                  </Col>
                                  <Col className='p-0'>
                                    <Form.Control
                                      name='fixedSuccessfulCommission'
                                      className={classNames(
                                        'form-control rounded-0 rounded-end text-black bg-white',
                                        {
                                          'is-invalid':
                                            errors.fixedSuccessfulCommission,
                                        },
                                      )}
                                      {...register(
                                        'fixedSuccessfulCommission',
                                        {
                                          required: 'This field is required',
                                          pattern: {
                                            value: /^(\d+(\.\d*)?|\.\d+)$/, // ✅ allows 123, 123.45, .45
                                            message: 'Please enter valid value',
                                          },
                                        },
                                      )}
                                    />
                                  </Col>
                                </Row>
                              </FormGroup>
                              <ErrorMessage
                                errors={errors}
                                name='fixedSuccessfulCommission'
                                render={({ message }) => (
                                  <div className='invalid-feedback d-block ms-1'>
                                    {message}
                                  </div>
                                )}
                              />
                            </div>
                          </div>
                          {generalSettings?.mangoPayModule !== 'yes' && (
                            <div className='row'>
                              <div className='form-group mb-3 ms-2'>
                                <label className='font-size-sm text-black'>
                                  {intl.formatMessage(
                                    { id: 'FLEX.FAIL.CAMP' },
                                    { value: taxonomy?.projectSingular },
                                  )}
                                  <span className='text-important ms-1'>*</span>
                                </label>
                              </div>
                              <div className='col-md-6 form-group mb-6'>
                                <FormGroup className='px-4'>
                                  <Row>
                                    <Col
                                      xs={'auto'}
                                      className='d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium'
                                    >
                                      <label id='percentageUnsuccessfulCommission'>{`Percentage (%)`}</label>
                                    </Col>
                                    <Col className='p-0'>
                                      <Form.Control
                                        name='percentageUnsuccessfulCommission'
                                        className={classNames(
                                          'form-control rounded-0 rounded-end text-black bg-white',
                                          {
                                            'is-invalid':
                                              errors.percentageUnsuccessfulCommission,
                                          },
                                        )}
                                        {...register(
                                          'percentageUnsuccessfulCommission',
                                          {
                                            required: 'This field is required',
                                            pattern: {
                                              value: /^(\d+(\.\d*)?|\.\d+)$/, // ✅ allows 123, 123.45, .45
                                              message:
                                                'Please enter valid value',
                                            },
                                          },
                                        )}
                                      />
                                    </Col>
                                  </Row>
                                </FormGroup>
                                <ErrorMessage
                                  errors={errors}
                                  name='percentageUnsuccessfulCommission'
                                  render={({ message }) => (
                                    <div className='invalid-feedback d-block ms-1'>
                                      {message}
                                    </div>
                                  )}
                                />
                              </div>
                              <div className='col-md-6 form-group mb-6'>
                                <FormGroup className='px-4'>
                                  <Row>
                                    <Col
                                      xs={'auto'}
                                      className='d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium'
                                    >
                                      <label id='fixedUnsuccessfulCommission'>{`Fixed (${generalSettings.currencyId?.symbol})`}</label>
                                    </Col>
                                    <Col className='p-0'>
                                      <Form.Control
                                        name='fixedUnsuccessfulCommission'
                                        className={classNames(
                                          'form-control rounded-0 rounded-end text-black bg-white',
                                          {
                                            'is-invalid':
                                              errors.fixedUnsuccessfulCommission,
                                          },
                                        )}
                                        {...register(
                                          'fixedUnsuccessfulCommission',
                                          {
                                            required: 'This field is required',
                                            pattern: {
                                              value: /^(\d+(\.\d*)?|\.\d+)$/, // ✅ allows 123, 123.45, .45
                                              message:
                                                'Please enter valid value',
                                            },
                                          },
                                        )}
                                      />
                                    </Col>
                                  </Row>
                                </FormGroup>
                                <ErrorMessage
                                  errors={errors}
                                  name='fixedUnsuccessfulCommission'
                                  render={({ message }) => (
                                    <div className='invalid-feedback d-block ms-1'>
                                      {message}
                                    </div>
                                  )}
                                />
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className='mb-6 bg-table-bg-clr p-4 rounded-fix'>
                      <div className='form-group bg-white p-4 rounded-fix d-flex justify-content-between align-items-center'>
                        <label className='font-size-sm text-black'>
                          Project Listing Fees
                        </label>
                        <Controller
                          name='sponserListingFeeStatus'
                          control={control}
                          defaultValue={false}
                          render={({ field }) => (
                            <Toggle
                              name='sponserListingFeeStatus'
                              icons={{ checked: null, unchecked: null }}
                              checked={field.value}
                              onChange={() => field.onChange(!field.value)}
                            />
                          )}
                        />
                      </div>
                      {watch('sponserListingFeeStatus') && (
                        <>
                          <div className='mt-5'>
                            <div className='form-group bg-white p-4 rounded-fix d-flex justify-content-between align-items-center'>
                              <span className='d-flex align-items-center justify-content-between'>
                                <label className='font-size-sm text-black'>
                                  Project Listing Fees Type
                                  <span className='text-danger ms-1'>*</span>
                                </label>
                              </span>
                              <div>
                                <div className='d-flex gap-5 gap-md-10 justify-content-end flex-wrap flex-md-nowrap'>
                                  <div className='custom-control custom-radio d-flex gap-2'>
                                    <Form.Check
                                      name='sponserListingFeeType'
                                      type='radio'
                                      id='sponserListingFeeType1'
                                      {...register('sponserListingFeeType', {
                                        required: 'This field is required',
                                      })}
                                      className=''
                                      value='SUBSCRIPTION'
                                      onChange={() => {
                                        setValue(
                                          'sponserListingFeeType',
                                          'SUBSCRIPTION',
                                        );
                                      }}
                                    />
                                    <label
                                      title=''
                                      htmlFor='sponserListingFeeType1'
                                      className='custom-control-label text-black font-size-sm'
                                    >
                                      Subscription
                                    </label>
                                  </div>
                                  <div className='custom-control custom-radio d-flex gap-2'>
                                    <Form.Check
                                      name='sponserListingFeeType'
                                      type='radio'
                                      id='sponserListingFeeType2'
                                      {...register('sponserListingFeeType')}
                                      className=''
                                      value='LISTING'
                                      onChange={() => {
                                        setValue(
                                          'sponserListingFeeType',
                                          'LISTING',
                                        );
                                      }}
                                    />
                                    <label
                                      title=''
                                      htmlFor='sponserListingFeeType2'
                                      className='custom-control-label text-black font-size-sm'
                                    >
                                      Fees Per Project
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <ErrorMessage
                              errors={errors}
                              name='sponserListingFeeType'
                              render={({ message }) => (
                                <div className='invalid-feedback d-block ms-1'>
                                  {message}
                                </div>
                              )}
                            />
                          </div>
                          {watch('sponserListingFeeType') === 'LISTING' && (
                            <div className='row mt-5'>
                              <div className='col-md-12 form-group mb-1'>
                                <label className='font-size-sm ms-2 text-black'>
                                  Fixed Fees Per Project
                                  <span className='text-danger ms-1'>*</span>
                                </label>

                                <FormGroup className='px-4'>
                                  <Row>
                                    <Col
                                      xs={'auto'}
                                      className='d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium'
                                    >
                                      <label id='sponserListingFlatFees'>{`Fixed (${generalSettings.currencyId?.symbol})`}</label>
                                    </Col>
                                    <Col className='p-0'>
                                      <Form.Control
                                        className={classNames(
                                          'form-control rounded-0 rounded-end text-black bg-white',
                                          {
                                            'is-invalid':
                                              errors.sponserListingFlatFees,
                                          },
                                        )}
                                        {...register('sponserListingFlatFees', {
                                          required: 'This field is required',
                                          pattern: {
                                            value: /^(\d+(\.\d*)?|\.\d+)$/, // ✅ allows 123, 123.45, .45
                                            message: 'Please enter valid value',
                                          },
                                        })}
                                      />
                                    </Col>
                                  </Row>
                                </FormGroup>
                              </div>
                              <ErrorMessage
                                errors={errors}
                                name='sponserListingFlatFees'
                                render={({ message }) => (
                                  <div className='invalid-feedback d-block ms-1'>
                                    {message}
                                  </div>
                                )}
                              />
                            </div>
                          )}
                          {watch('sponserListingFeeType') ===
                            'SUBSCRIPTION' && (
                            <>
                              <div className='mt-5'>
                                <div className='form-group bg-white p-4 rounded-fix d-flex justify-content-between align-items-center'>
                                  <span className='d-flex align-items-center justify-content-between'>
                                    <label className='font-size-sm text-black'>
                                      Subscription Duration
                                      <span className='text-danger ms-1'>
                                        *
                                      </span>
                                    </label>
                                  </span>
                                  <div>
                                    <div className='d-flex justify-content-end gap-5 gap-md-10 flex-wrap flex-md-nowrap'>
                                      <div className='custom-control custom-radio d-flex gap-2'>
                                        <Form.Check
                                          name='sponserSubscriptionDuration'
                                          type='radio'
                                          id='sponserSubscriptionDuration1'
                                          {...register(
                                            'sponserSubscriptionDuration',
                                            {
                                              required:
                                                'This field is required',
                                            },
                                          )}
                                          className=''
                                          value='MONTHLY'
                                          onChange={() => {
                                            setValue(
                                              'sponserSubscriptionDuration',
                                              'MONTHLY',
                                            );
                                          }}
                                        />
                                        <label
                                          title=''
                                          htmlFor='sponserSubscriptionDuration1'
                                          className='custom-control-label font-size-sm text-black'
                                        >
                                          MONTHLY
                                        </label>
                                      </div>
                                      <div className='custom-control custom-radio d-flex gap-2'>
                                        <Form.Check
                                          name='sponserSubscriptionDuration'
                                          type='radio'
                                          id='sponserSubscriptionDuration2'
                                          {...register(
                                            'sponserSubscriptionDuration',
                                          )}
                                          className=''
                                          value='QUARTERLY'
                                          onChange={() => {
                                            setValue(
                                              'sponserSubscriptionDuration',
                                              'QUARTERLY',
                                            );
                                          }}
                                        />
                                        <label
                                          title=''
                                          htmlFor='sponserSubscriptionDuration2'
                                          className='custom-control-label font-size-sm text-black'
                                        >
                                          QUARTERLY
                                        </label>
                                      </div>
                                      <div className='custom-control custom-radio d-flex gap-2'>
                                        <Form.Check
                                          name='sponserSubscriptionDuration'
                                          type='radio'
                                          id='sponserSubscriptionDuration3'
                                          {...register(
                                            'sponserSubscriptionDuration',
                                          )}
                                          className=''
                                          value='YEARLY'
                                          onChange={() => {
                                            setValue(
                                              'sponserSubscriptionDuration',
                                              'YEARLY',
                                            );
                                          }}
                                        />
                                        <label
                                          title=''
                                          htmlFor='sponserSubscriptionDuration3'
                                          className='custom-control-label font-size-sm text-black'
                                        >
                                          YEARLY
                                        </label>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <ErrorMessage
                                  errors={errors}
                                  name='sponserSubscriptionDuration'
                                  render={({ message }) => (
                                    <div className='invalid-feedback d-block ms-1'>
                                      {message}
                                    </div>
                                  )}
                                />
                              </div>
                              {generalSettings?.walletModule === 'yes' &&
                              !watch('stripeAutoDeductStatus') ? (
                                <div className='mt-5 form-group bg-white p-4 rounded-fix d-flex justify-content-between align-items-center'>
                                  <label className='text-black font-size-sm'>
                                    Wallet Auto Deduct
                                    <span className='text-important mx-1'>
                                      *
                                    </span>
                                    <OverlayTrigger
                                      overlay={
                                        <Tooltip id='wallet-deduct-tooltip'>
                                          When it's turned ON, the subscription
                                          plan will be auto renewed and amount
                                          deducted from user's wallet
                                        </Tooltip>
                                      }
                                    >
                                      <span className='tooltip-wrapper'>
                                        <i className='fa fa-question-circle text-black' />
                                      </span>
                                    </OverlayTrigger>
                                  </label>
                                  <Controller
                                    name='walletAutoDeductStatus'
                                    control={control}
                                    defaultValue={false}
                                    render={({ field }) => (
                                      <Toggle
                                        name='walletAutoDeductStatus'
                                        icons={{
                                          checked: null,
                                          unchecked: null,
                                        }}
                                        checked={field.value}
                                        onChange={() =>
                                          field.onChange(!field.value)
                                        }
                                      />
                                    )}
                                  />
                                </div>
                              ) : null}
                              {payments?.paymentType === 'stripe' &&
                              payments?.status === 'yes' &&
                              !watch('walletAutoDeductStatus') ? (
                                <div className='mt-5 form-group bg-white p-4 rounded-fix d-flex justify-content-between align-items-center'>
                                  <label className='text-black font-size-sm'>
                                    Stripe Auto Deduct
                                    <span className='text-important ms-1'>
                                      *
                                    </span>
                                    <OverlayTrigger
                                      overlay={
                                        <Tooltip id='pending-actions-tooltip'>
                                          When it's turned ON, the subscription
                                          plan will be auto renewed and amount
                                          deducted from user's card used on
                                          first subscription
                                        </Tooltip>
                                      }
                                    >
                                      <span className='tooltip-wrapper'>
                                        <i className='fa fa-question-circle text-black' />
                                      </span>
                                    </OverlayTrigger>
                                  </label>
                                  <Controller
                                    name='stripeAutoDeductStatus'
                                    control={control}
                                    defaultValue={false}
                                    render={({ field }) => (
                                      <Toggle
                                        name='stripeAutoDeductStatus'
                                        icons={{
                                          checked: null,
                                          unchecked: null,
                                        }}
                                        checked={field.value}
                                        onChange={() =>
                                          field.onChange(!field.value)
                                        }
                                      />
                                    )}
                                  />
                                </div>
                              ) : null}
                            </>
                          )}
                        </>
                      )}
                    </div>
                    {watch('sponserListingFeeStatus') &&
                      watch('sponserListingFeeType') === 'LISTING' && (
                        <>
                          <div className='mb-6 bg-table-bg-clr p-4 rounded-fix'>
                            <div className='form-group bg-white p-4 rounded-fix d-flex justify-content-between align-items-center'>
                              <label className='font-size-sm text-black'>
                                Marketing Resources Fees
                              </label>
                              <Controller
                                name='sponserMarketingResourceFeeStatus'
                                control={control}
                                defaultValue={false}
                                render={({ field }) => (
                                  <Toggle
                                    name='sponserMarketingResourceFeeStatus'
                                    icons={{ checked: null, unchecked: null }}
                                    checked={field.value}
                                    onChange={() =>
                                      field.onChange(!field.value)
                                    }
                                  />
                                )}
                              />
                            </div>
                            {watch('sponserMarketingResourceFeeStatus') && (
                              <div className='row'>
                                <div className='col-md-6 form-group mb-1 mt-5'>
                                  <label className='font-size-sm mb-2 ms-2 text-black'>
                                    Marketing Resources Fixed Fees
                                    <span className='text-danger ms-1'>*</span>
                                  </label>

                                  <FormGroup className='px-4'>
                                    <Row>
                                      <Col
                                        xs={'auto'}
                                        className='d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium'
                                      >
                                        <label id='sponserMarketingResourceFlatFees'>{`Fixed (${generalSettings.currencyId?.symbol})`}</label>
                                      </Col>
                                      <Col className='p-0'>
                                        <Form.Control
                                          className={classNames(
                                            'form-control rounded-0 rounded-end text-black bg-white',
                                            {
                                              'is-invalid':
                                                errors.sponserMarketingResourceFlatFees,
                                            },
                                          )}
                                          {...register(
                                            'sponserMarketingResourceFlatFees',
                                            {
                                              required:
                                                'This field is required',
                                              pattern: {
                                                value: /^(\d+(\.\d*)?|\.\d+)$/, // ✅ allows 123, 123.45, .45
                                                message:
                                                  'Please enter valid value',
                                              },
                                            },
                                          )}
                                        />
                                      </Col>
                                    </Row>
                                  </FormGroup>
                                  <ErrorMessage
                                    errors={errors}
                                    name='sponserMarketingResourceFlatFees'
                                    render={({ message }) => (
                                      <div className='invalid-feedback d-block ms-1'>
                                        {message}
                                      </div>
                                    )}
                                  />
                                </div>
                                <div className='col-md-6 mt-5'>
                                  <div className='form-group floating-input-field-wrapper-textarea bg-white'>
                                    <label className='floating-input-field-label-static start-0 ms-4'>
                                      Marketing Resources Fees Description
                                      <span className='text-danger ms-1'>
                                        *
                                      </span>
                                    </label>
                                    <Form.Control
                                      as='textarea'
                                      rows={2}
                                      className={classNames(
                                        'floating-input-field-item bg-white',
                                        {
                                          'is-invalid':
                                            errors.sponserMarketingResourceDescription,
                                        },
                                      )}
                                      {...register(
                                        'sponserMarketingResourceDescription',
                                        {
                                          required: 'This field is required',
                                        },
                                      )}
                                    />
                                  </div>
                                  <ErrorMessage
                                    errors={errors}
                                    name='sponserMarketingResourceDescription'
                                    render={({ message }) => (
                                      <div className='invalid-feedback d-block ms-1'>
                                        {message}
                                      </div>
                                    )}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                          <div className='mb-6 bg-table-bg-clr p-4 rounded-fix'>
                            <div className='form-group bg-white p-4 rounded-fix d-flex justify-content-between align-items-center'>
                              <label className='font-size-sm text-black'>
                                Sales Resources Fees
                              </label>
                              <Controller
                                name='sponserSalesResourceFeeStatus'
                                control={control}
                                defaultValue={false}
                                render={({ field }) => (
                                  <Toggle
                                    name='sponserSalesResourceFeeStatus'
                                    icons={{ checked: null, unchecked: null }}
                                    checked={field.value}
                                    onChange={() =>
                                      field.onChange(!field.value)
                                    }
                                  />
                                )}
                              />
                            </div>
                            {watch('sponserSalesResourceFeeStatus') && (
                              <div className='row'>
                                <div className='col-md-6 form-group mb-1 mt-5'>
                                  <label className='font-size-sm ms-2 text-black'>
                                    Sales Resources Fixed Fees
                                    <span className='text-danger ms-1'>*</span>
                                  </label>

                                  <FormGroup className='px-4'>
                                    <Row>
                                      <Col
                                        xs={'auto'}
                                        className='d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium'
                                      >
                                        <label id='sponserSalesResourceFlatFees'>{`Fixed (${generalSettings.currencyId?.symbol})`}</label>
                                      </Col>
                                      <Col className='p-0'>
                                        <Form.Control
                                          className={classNames(
                                            'form-control rounded-0 rounded-end text-black bg-white',
                                            {
                                              'is-invalid':
                                                errors.sponserSalesResourceFlatFees,
                                            },
                                          )}
                                          {...register(
                                            'sponserSalesResourceFlatFees',
                                            {
                                              required:
                                                'This field is required',
                                              pattern: {
                                                value: /^(\d+(\.\d*)?|\.\d+)$/, // ✅ allows 123, 123.45, .45
                                                message:
                                                  'Please enter valid value',
                                              },
                                            },
                                          )}
                                        />
                                      </Col>
                                    </Row>
                                  </FormGroup>
                                  <ErrorMessage
                                    errors={errors}
                                    name='sponserSalesResourceFlatFees'
                                    render={({ message }) => (
                                      <div className='invalid-feedback d-block ms-1'>
                                        {message}
                                      </div>
                                    )}
                                  />
                                </div>
                                <div className='col-md-6 mt-5'>
                                  <div className='form-group floating-input-field-wrapper-textarea bg-white'>
                                    <label className='floating-input-field-label-static start-0 ms-4'>
                                      Sales Resources Fees Description
                                      <span className='text-danger ms-1'>
                                        *
                                      </span>
                                    </label>
                                    <Form.Control
                                      as='textarea'
                                      rows={2}
                                      className={classNames(
                                        'floating-input-field-item bg-white',
                                        {
                                          'is-invalid':
                                            errors.sponserSalesResourceDescription,
                                        },
                                      )}
                                      {...register(
                                        'sponserSalesResourceDescription',
                                        {
                                          required: 'This field is required',
                                        },
                                      )}
                                    />
                                  </div>
                                  <ErrorMessage
                                    errors={errors}
                                    name='sponserSalesResourceDescription'
                                    render={({ message }) => (
                                      <div className='invalid-feedback d-block ms-1'>
                                        {message}
                                      </div>
                                    )}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    {demoMode === 'false' ? (
                      <button
                        type='submit'
                        className='btn btn-secondary common-save-btn mb-2'
                        disabled={isMutating}
                      >
                        {isMutating ? (
                          <span
                            className='spinner-border spinner-border-sm'
                            role='status'
                            aria-hidden='true'
                          ></span>
                        ) : (
                          intl.formatMessage({ id: 'SAVE' })
                        )}
                      </button>
                    ) : null}
                  </Form>
                </Col>
              </Row>
            </CardBody>
          </Card>
          {watch('sponserListingFeeType') === 'SUBSCRIPTION' && (
            <SponserSubscriptionsList
              generalSettings={generalSettings}
              demoMode={demoMode}
            />
          )}
        </>
      ) : (
        <>
          <Card>
            <CardHeader title='Revenue Fees' />
            <CryptoRevenueForm />
          </Card>
          <Card className='mt-5'>
            <CardHeader title='Revenue Fees (Tokenization)' />
            <TokenizationRevenueForm />
          </Card>
        </>
      )}
    </>
  );
};
export default compose(withTranslation())(SponsorFees);
